import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { useGetRequestStartingPoint } from "api/queries/common/useGetRequestStartingPoint";
import { useGetPaymentLocationFormDetails } from "api/queries/location/useGetPaymentLocationFormDetails";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { getPaymentLocationDetailsParams } from "@RequestFormV2/utils/getPaymentLocationDetailsParams";

const toSelectOptions = (values: Array<{ key: string; value: string }>) =>
  values.map(({ key, value }) => ({ label: key, value }));

const CUSTOM_FIELD_IDS = new Set([
  "cost_center",
  "custom_field_01",
  "custom_field_02",
  "custom_field_03",
  "reason_code",
  "reason_code_group",
]);

export function useOrganisationData() {
  const mode = useRequestFormMode();
  const draftId = useRequestFormDraftId();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLocation = useWatch({ name: "organizational.location" });

  // /starting-point
  const { data: startingPoint, isLoading: isStartingPointLoading } = useGetRequestStartingPoint({});

  // /location/{location}/details
  const { data: locationDetails, isLoading: isLocationDetailsLoading } = useGetPaymentLocationFormDetails({
    location: selectedLocation ?? "",
    config: {
      params: getPaymentLocationDetailsParams(mode, selectedLocation ?? "", draftId).params,
    },
    options: { enabled: !!selectedLocation },
    isQuote: mode === "quote",
  });

  useEffect(() => {
    if (mode !== "quick" || draftId || !locationDetails?.shipment_draft_reference) return;

    searchParams.set("draft_id", locationDetails.shipment_draft_reference);
    setSearchParams(searchParams);
  }, [draftId, mode, locationDetails?.shipment_draft_reference, searchParams, setSearchParams]);

  const locationOptions = useMemo(() => {
    if (!startingPoint?.locations) return [];
    return [...startingPoint.locations]
      .filter((item) => item.available)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [startingPoint?.locations]);

  const invoiceLocationDetails = useMemo(() => {
    if (!locationDetails) return undefined;

    const invoiceLocation = locationDetails.organizational_options.find((f) => f.field_id === "invoice_location");
    if (!invoiceLocation) return undefined;

    return {
      ...invoiceLocation,
      dropdown_values: Array.isArray(invoiceLocation.dropdown_values)
        ? toSelectOptions(invoiceLocation.dropdown_values)
        : [],
    };
  }, [locationDetails]);

  const shipmentTitleDetails = useMemo(
    () => locationDetails?.organizational_options.find((f) => f.field_id === "shipment_title"),
    [locationDetails],
  );

  const longField01 = useMemo(
    () => locationDetails?.organizational_options.find((f) => f.field_id === "custom_long_field_01"),
    [locationDetails],
  );

  const organisationFields = useMemo(() => {
    const rawOptions = locationDetails?.organizational_options;
    if (!rawOptions?.length) return [];

    const fields = rawOptions
      .filter((item) => item.field_id)
      .map((item) => {
        if (!item.dropdown_values?.length) return item;
        return {
          ...item,
          dropdown_values: [...item.dropdown_values]
            .sort((a, b) => a.key?.localeCompare(b.key))
            .map((val) => ({ ...val, ...toSelectOptions([val])[0] })),
        };
      });

    const order = locationDetails?.order ?? [];
    const customFields = fields.filter((item) => CUSTOM_FIELD_IDS.has(item.field_id));
    const otherFields = fields.filter((item) => !CUSTOM_FIELD_IDS.has(item.field_id));

    const orderedCustomFields = [...customFields].sort((a, b) => order.indexOf(a.field_id) - order.indexOf(b.field_id));

    const reasonCodeGroupIds = orderedCustomFields.findIndex((f) => f.field_id === "reason_code_group");
    const reasonCodeIds = orderedCustomFields.findIndex((f) => f.field_id === "reason_code");
    if (reasonCodeGroupIds !== -1 && reasonCodeIds !== -1 && reasonCodeGroupIds > reasonCodeIds) {
      const [rcg] = orderedCustomFields.splice(reasonCodeGroupIds, 1);
      orderedCustomFields.splice(reasonCodeIds, 0, rcg);
    }

    return [...otherFields, ...orderedCustomFields];
  }, [locationDetails]);

  return useMemo(
    () => ({
      startingPoint,
      isStartingPointLoading,
      locationOptions,
      selectedLocation,
      isLocationDetailsLoading,
      invoiceLocationDetails,
      organisationFields,
      shipmentTitleDetails,
      longField01,
    }),
    [
      startingPoint,
      isStartingPointLoading,
      locationOptions,
      selectedLocation,
      isLocationDetailsLoading,
      invoiceLocationDetails,
      organisationFields,
      shipmentTitleDetails,
      longField01,
    ],
  );
}
